'use client'
import React, { FC } from 'react'
import classNames from 'classnames'

import event from '@/utils/event'
import Wallet from '@/utils/wallet'
import useLoggedIn, { useLogout } from '@/utils/hooks/useLoggedIn'
import { useAdminCheck } from '@/utils/hooks/useAdminMetrics'
import { useRouter } from 'next/navigation'
import { getFirebasePushToken } from '@/utils/firebase-messaging'
import SafeLocalStorage from '@/utils/safe-local-storage'
import {
	STORAGE_TAPROOT_ADDRESS,
	FIREBASE_CF_GET_TOKEN_STATUS_API_URL,
	FIREBASE_CF_REMOVE_TOKEN_API_URL,
	FIREBASE_CF_SAVE_TOKEN_API_URL,
	API_URL
} from '@/utils/constants'
import Link from '@/components/link'
import SettingCard from '../setting-card'
import { ChevronLeft } from '@mui/icons-material'

import jwtAndEncrypt from '@/utils/jwt-and-encrypt'
import axios from 'axios'
import styles from './SettingsDetail.module.css'
import { ensureSession } from '@/utils/wallet/session'

const EVENT = 'floating-menu'
let init = false

const SettingsDetail: FC<{ className?: string }> = (props) => {
	const { className } = props
	const [_counter, setCounter] = React.useState(() => Math.random())
	const [_viewMenu, setViewMenu] = React.useState(() => Wallet.settings.floatingMenu())
	const [pushOn, setPushOn] = React.useState(false)
	const [pushNotificationsTogglable, setPushNotificationsTogglable] = React.useState(false)
	const router = useRouter()
	const logout = useLogout()
	const loggedIn = useLoggedIn()
	const { isAdmin } = useAdminCheck(loggedIn ? Wallet.getTaprootAddress() : null)
	const isInStandaloneMode = React.useCallback(
		() => 'standalone' in window?.navigator && window?.navigator?.standalone,
		[]
	)

	React.useEffect(() => {
		async function run() {
			await Wallet.fetchProfile()
			setCounter(Math.random())
		}

		if (init) {
			return
		}

		init = true
		run()
	}, [])

	const getPushStatus = React.useCallback(() => {
		if (typeof Notification !== 'undefined' && isInStandaloneMode()) {
			if (Notification.permission === 'granted') {
				getFirebasePushToken()
					.then(async (currentToken) => {
						if (currentToken) {
							var walletAddress = SafeLocalStorage.getItem(STORAGE_TAPROOT_ADDRESS)
							var body = await jwtAndEncrypt(walletAddress, currentToken)
							axios
								.post(`${FIREBASE_CF_GET_TOKEN_STATUS_API_URL}`, body)
								.then((res) => {
									if (res?.status === 200) {
										setPushOn(true)
									} else {
										setPushOn(false)
									}
								})
								.catch((e) => {
									setPushOn(false)
									console.log(e)
								})
						}
					})
					.catch((err) => {
						console.log('An error occurred while retrieving token. ', err)
					})
			} else {
				setPushOn(false)
			}
		} else {
			setPushOn(false)
		}
	}, [isInStandaloneMode])

	const togglePush = () => {
		if (pushOn) {
			Notification.requestPermission().then((permission) => {
				if (permission === 'granted') {
					getFirebasePushToken()
						.then(async (currentToken) => {
							if (currentToken) {
								var walletAddress = SafeLocalStorage.getItem(STORAGE_TAPROOT_ADDRESS)
								var body = await jwtAndEncrypt(walletAddress, currentToken)
								axios
									.post(`${FIREBASE_CF_REMOVE_TOKEN_API_URL}`, body)
									.then((_res) => {
										setPushOn(false)
									})
									.catch((e) => {
										console.log(e)
									})
							}
						})
						.catch((err) => {
							console.log('An error occurred while retrieving token. ', err)
						})
				}
			})
		} else {
			Notification.requestPermission().then((permission) => {
				if (permission === 'granted') {
					getFirebasePushToken()
						.then(async (currentToken) => {
							if (currentToken) {
								var walletAddress = SafeLocalStorage.getItem(STORAGE_TAPROOT_ADDRESS)
								var body = await jwtAndEncrypt(walletAddress, currentToken)
								axios
									.post(`${FIREBASE_CF_SAVE_TOKEN_API_URL}`, body)
									.then((_res) => {
										setPushOn(true)
									})
									.catch((e) => {
										console.log(e)
									})
							}
						})
						.catch((err) => {
							console.log('An error occurred while retrieving token. ', err)
						})
				}
			})
		}
	}

	const twitter_handle = Wallet.getProfile()?.twitter_handle

	const settings = [
		{
			title: 'Business dashboard',
			description: 'Revenue, users and sales across the marketplace',
			enabled: isAdmin,
			admin: true,
			onClick: () => {
				router.push('/admin/business')
			}
		},
		{
			title: 'View recovery',
			description: 'Back up your 12 word seed phrase',
			enabled: Wallet.type() === 'ordinalswallet',
			onClick: () => {
				event.emit('open-password-modal', { showSeed: true })
			}
		},
		{
			title: 'Sign message',
			description: 'Cryptographically sign messages',
			enabled: Wallet.type() === 'ordinalswallet',
			onClick: () => {
				router.push('/sign-message')
			}
		},
		{
			title: 'Lock wallet',
			description: 'Force password to send money',
			enabled: Wallet.type() === 'ordinalswallet'
		},
		{
			title: 'Connect Twitter',
			description: twitter_handle
				? `Connected as @${twitter_handle}`
				: `Verify your twitter account`,
			enabled: true,
			button: twitter_handle ? 'Disconnect' : 'Connect',
			onClick: async () => {
				const signature = await ensureSession()

				if (twitter_handle) {
					await axios.post(`${API_URL}/twitter/disconnect`, {
						address: Wallet.getTaprootAddress(),
						signature
					})
					await Wallet.fetchProfile()
					setCounter(Math.random())
				} else {
					const { data } = await axios.post(`${API_URL}/twitter/connect`, {
						address: Wallet.getTaprootAddress(),
						signature
					})
					window.location = data.url
				}
			}
		},
		{
			title: 'Get notifications',
			description: 'Turn on push notifications',
			enabled: pushNotificationsTogglable,
			toggle: true,
			toggleChecked: pushOn,
			onToggle: () => {
				togglePush()
			}
		},
		{
			title: 'Sign out',
			description: 'Sign out of Ordinals Wallet',
			enabled: Wallet.type() === 'ordinalswallet',
			onClick: () => event.emit('open-signout-modal')
		},
		{
			title: 'Sign out',
			description: 'Sign out of Ordinals Wallet',
			enabled: Wallet.type() !== 'ordinalswallet',
			onClick: () => {
				logout()
			}
		}
	].filter((e) => e.enabled)

	React.useEffect(() => {
		requestAnimationFrame(() => {
			getPushStatus()
		})
		const handleEvent = () => {
			setViewMenu(Wallet.settings.floatingMenu())
		}

		const nextPushNotificationsTogglable = Boolean(
			isInStandaloneMode() && typeof Notification !== 'undefined'
		)
		requestAnimationFrame(() => {
			setPushNotificationsTogglable(nextPushNotificationsTogglable)
		})

		event.listen(EVENT, handleEvent)

		return () => {
			event.removeListener(EVENT, handleEvent)
		}
	}, [getPushStatus, isInStandaloneMode])

	return (
		<div className={classNames({ [props.className as any]: !!className }, styles.root)}>
			<Link href={'/wallet'} className={styles.goBack}>
				<ChevronLeft className={styles.chevron} />
				<span className={classNames('text-sm', 'regular')}>Go back</span>
			</Link>
			{settings.map((params, i) => (
				<SettingCard {...params} key={i} />
			))}
		</div>
	)
}

export default SettingsDetail
